/**
 * BusinessDetail.jsx — public + admin view for /business/:slug.
 * Anonymous visitors see verified listings' contact info. Admins see
 * everything plus edit / upload / verification-history controls.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MapPin, Phone, MessageCircle, Globe, ShieldCheck, ShieldAlert, Loader2, Upload, Check } from "lucide-react";

const TIER_STYLE = {
  unverified:          { fg: "#B45309", bg: "#FEF3C7", label: "Unverified" },
  phone_verified:      { fg: "#075985", bg: "#E0F2FE", label: "Phone-verified" },
  documents_submitted: { fg: "#5B4A73", bg: "#DDD6FE", label: "Documents submitted" },
  field_verified:      { fg: "#065F46", bg: "#D1FAE5", label: "Verified" },
};

const DOC_TYPES = [
  { v: "gst_certificate",     label: "GST Certificate" },
  { v: "trade_licence",       label: "Trade Licence" },
  { v: "shop_act",            label: "Shop Act Registration" },
  { v: "society_registration",label: "Society Registration" },
];

export default function BusinessDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docUrl, setDocUrl] = useState("");
  const [docType, setDocType] = useState("gst_certificate");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/ift/businesses/${slug}`);
      setBiz(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Business not found");
      setBiz(null);
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = !!biz && (biz.owner_user_id === user?.id || (biz.admins || []).some((a) => a.user_id === user?.id) || user?.is_admin);

  const submitDoc = async () => {
    if (!docUrl.trim()) return toast.error("Paste the URL of the uploaded document");
    setUploading(true);
    try {
      await http.post(`/ift/businesses/${biz.id}/documents`, { doc_type: docType, file_url: docUrl.trim() });
      toast.success("Document recorded — listing promoted to Documents Submitted");
      setDocUrl("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record document");
    } finally { setUploading(false); }
  };

  if (loading) return <div className="p-8 text-center text-sm opacity-60"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>;
  if (!biz) return <div className="p-8 text-center">Not found.</div>;

  const tier = TIER_STYLE[biz.verification_status] || TIER_STYLE.unverified;
  const loc = biz.primary_location || {};
  const cityLine = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");

  return (
    <div className="max-w-3xl mx-auto py-8 px-4" data-testid={`biz-detail-${biz.id}`}>
      <div className="wofy-card">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>{biz.business_category_label}</div>
            <h1 className="font-display text-3xl">{biz.business_name}</h1>
            <div className="text-sm opacity-70 mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {cityLine}</div>
          </div>
          <span
            className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full inline-flex items-center gap-1.5"
            style={{ background: tier.bg, color: tier.fg }}
            data-testid="biz-detail-tier"
          >
            {biz.verification_status === "field_verified" ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
            {tier.label}
          </span>
        </div>

        {biz.about && <p className="text-sm mb-4 whitespace-pre-wrap">{biz.about}</p>}

        <div className="flex flex-wrap gap-2 mb-4">
          {(biz.business_subtypes || []).map((s) => (
            <span key={s} className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full capitalize" style={{ background: "var(--wofy-surface)" }}>{s}</span>
          ))}
        </div>

        {(biz.services_offered || []).length > 0 && (
          <div className="mb-4">
            <div className="overline mb-2">SERVICES</div>
            <div className="flex flex-wrap gap-1.5">
              {biz.services_offered.map((s) => (
                <span key={s} className="text-xs px-2 py-1 rounded-md" style={{ background: "var(--wofy-surface)" }}>{s.replace(/_/g, " ")}</span>
              ))}
            </div>
          </div>
        )}

        {biz.hours?.emergency_available_24_7 && (
          <div className="text-xs rounded-lg p-2 mb-4 inline-flex items-center gap-1.5" style={{ background: "#FEF3C7", color: "#7A5A2E" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Available 24/7 for emergencies
          </div>
        )}

        {/* Contact — only when verified or when the visitor is an admin */}
        {(biz.business_phone_e164 || biz.whatsapp_e164 || biz.business_email || biz.website) ? (
          <div className="rounded-lg border p-3 mb-4" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-detail-contact">
            <div className="overline mb-2">CONTACT</div>
            <div className="flex flex-wrap gap-3 text-sm">
              {biz.business_phone_e164 && <a href={`tel:${biz.business_phone_e164}`} className="inline-flex items-center gap-1 underline"><Phone className="w-3.5 h-3.5" /> {biz.business_phone_e164}</a>}
              {biz.whatsapp_e164 && <a href={`https://wa.me/${biz.whatsapp_e164.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>}
              {biz.business_email && <a href={`mailto:${biz.business_email}`} className="underline">{biz.business_email}</a>}
              {biz.website && <a href={biz.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><Globe className="w-3.5 h-3.5" /> Website</a>}
            </div>
          </div>
        ) : (
          <div className="rounded-lg p-3 mb-4 text-xs opacity-70" style={{ background: "var(--wofy-surface)" }}>
            Contact details unlock after documents-submitted verification.
          </div>
        )}

        {isAdmin && (
          <>
            <div className="rounded-lg border p-3 mb-4" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-detail-admin">
              <div className="overline mb-2">ADMIN · UPLOAD VERIFICATION DOC</div>
              <div className="flex flex-wrap gap-2 items-center">
                <select value={docType} onChange={(e) => setDocType(e.target.value)} className="rounded-lg border px-2 py-1.5 text-xs" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-doc-type">
                  {DOC_TYPES.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                </select>
                <input value={docUrl} onChange={(e) => setDocUrl(e.target.value)} placeholder="URL of the uploaded file (Drive / Storage)" className="flex-1 rounded-lg border px-3 py-1.5 text-xs" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-doc-url" />
                <button onClick={submitDoc} disabled={uploading} className="wofy-btn-primary text-xs" data-testid="biz-doc-submit">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Record
                </button>
              </div>
              <p className="text-[11px] opacity-60 mt-2">
                Recording a document auto-promotes your listing to <b>Documents Submitted</b> and unlocks the public contact section.
              </p>
            </div>

            {(biz.verification_history || []).length > 0 && (
              <div className="rounded-lg border p-3 mb-4 text-xs" style={{ borderColor: "var(--wofy-line)" }} data-testid="biz-detail-history">
                <div className="overline mb-2">VERIFICATION HISTORY</div>
                <ul className="space-y-1">
                  {biz.verification_history.map((h, i) => (
                    <li key={i}><b>{h.tier}</b> — <span className="opacity-70">{h.note || "no note"} · {new Date(h.at).toLocaleString()}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
